import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSpring, animated } from 'react-spring';
import { Box, Typography, Button, IconButton, Grid, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './../BottomSheet.css';
import { useDrag } from 'react-use-gesture';
import swal from 'sweetalert';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomToast from './CustomToast';
import config from '../config';

const BottomSheetClosePosition = ({ userDetails, position, stock, isOpen, onClose }) => {
  const { refreshTradesAndPortfolio, submitRejectionLog } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const [disableBtn, setDisable] = useState(false);
  const [orderExecutionType, setOrderExecutionType] = useState('Market');
  const [price, setPrice] = useState('');
  const [lots, setLots] = useState(1);
  const [quantity, setQuantity] = useState(position?.buyQty > 0 ? position?.buyQty : position?.sellQty);

  const styles = useSpring({
    transform: isOpen ? 'translateY(0%)' : 'translateY(100%)',
  });

  const [{ y }, api] = useSpring(() => ({ y: 0 }));

  React.useEffect(() => {
    api.start({ y: isOpen ? 0 : 100 });
  }, [isOpen, api]);

  // Add swipe functionality
  const bind = useDrag(
    ({ down, movement: [, my], direction: [, dy], velocity }) => {
      if (down && dy > 0 && velocity > 0.2) {
        onClose();
      } else {
        api.start({ y: down ? my : 0 });
      }
    },
    { from: () => [0, y.get()] }
  );

  // Initialize values when position changes
  useEffect(() => {
    if (isOpen && position) {
      setLots((position?.buyQty > 0 ? position?.buyQty : position?.sellQty)/position?.lotSize);
      setQuantity(position?.buyQty > 0 ? position?.buyQty : position?.sellQty);
      setPrice('');
      setOrderExecutionType('Market');
    }
  }, [isOpen, position]);

  const showToast = (type, message) => {
    if(type==1){
      toast.success(<CustomToast title={"Trade Placed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==2){
      toast.success(<CustomToast title={"Order Placed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==3){
      toast.error(<CustomToast title={"Order Failed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
  };

  const handleOrderTypeToggle = (orderExecutionType) => {
    setOrderExecutionType(orderExecutionType);
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('backdrop')) {
      onClose();
    }
  };

  // Get live prices
  const _Bid = stock?.Bid || position?.buyPrice || 0;
  const _Ask = stock?.Ask || position?.sellPrice || 0;
  const _LTP = stock?.lastTradedPrice || position?.lastTradedPrice || 0;

  const createOrder = async () => {
    setDisable(true);
    
    // Determine order type based on position side
    const orderType = position.buyQty > 0 ? 2 : 1; // If long position, sell to close; if short position, buy to close
    
    if (quantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);
      return;
    }

    if (_Bid <= 0 || _Ask <= 0 || _Bid == _Ask) {
      alert("Price not updated, please refresh");
      setDisable(false);
      return;
    }

    if(orderExecutionType=="Limit" || orderExecutionType=="SL"){
      if(Number(price) < Number(position.lower_circuit) || Number(price) > Number(position.upper_circuit)) {
        alert("Order price is beyond daily price range.");
        setDisable(false);
        return;
      }
    }

    if (orderExecutionType=="Limit") {
      if(price == _LTP){
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      if (orderType === 1) {
        if (price >= _Bid) {
          alert("Order price must be less than bid price.");
          setDisable(false);
          return;
        }
      } else {
        if (price <= _Ask) {
          alert("Order price must be greater than ask price.");
          setDisable(false);
          return;
        }
      }
    }
    else if (orderExecutionType=="SL") { 
      if(price == _LTP){
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      if(orderType==2){
        if(price >= _Bid){
          alert("Order price should be less than bid price."); 
          setDisable(false);
          return;
        }
      }
      else {
        if(price <= _Ask){
          alert("Order price should be greater than ask price."); 
          setDisable(false);
          return;
        }
      }
    }

    if (lots <= 0) {
      alert("Problem with lot quantity.");
      setDisable(false);
      return;
    }

    // Prepare position data for API
    const positionData = {
      ...position,
      dispName: position.dispname || position.stockName,
      stock: {
        instrumentIdentifier: position.stockId,
        exchange: position.exchange,
        dispName: position.stockName,
        ...stock
      }
    };

    try {
      const response = await fetch(`${config.APP_BASE_URL}mediator_rfs.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stock: positionData,
          orderTypeBuySell: orderType,
          orderExecutionType: orderExecutionType=="Market" ? 1 : (orderExecutionType=="Limit" ? 2 : 3),
          lots: lots,
          quantity: quantity,
          orderTransactionType: 2,
          orderDevice: 1,
          limitSlPrice: price,
          user_id: userDetails.clientId,
          parent_id: userDetails.parentId,
          ip: process.env.REACT_APP_USER_IP,
        }),
      });
      
      const data = await response.json();

      if (!data.success) {
        let msg = data.message;
        
        if(data.message=="SL Position"){
          msg = "Please remove SL from trades.";
        }
        else if(data.message=="Position already closed."){
          msg = "Position already closed.";
        } else {
          msg = data.message;
        }

        showToast(3, msg);
        setDisable(false);
      } else {
        setDisable(false);
        onClose();

        const b_s = orderType == 1 ? 'Buy' : 'Sell';
        const msg = b_s+" "+Number(lots).toFixed(2)+" Lot(s) of "+position.stockName+" @"+Number(data.orderPrice).toFixed(2);

        if (orderExecutionType=="Limit" || orderExecutionType=="SL"){
          swal({
            title: orderExecutionType+" Order Successful",
            text: msg,
            icon: "success",
            buttons: {
              confirm: "OK"
            },
          });
        } else {
          swal({
            title: "Order Successful",
            text: msg,
            icon: "success",
            buttons: {
              confirm: "OK"
            },
          });
        }

        refreshTradesAndPortfolio('both');
      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
    }
  };

  const handleLotsChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setLots(value);
    setQuantity(value * (position?.lotSize || 1));
  };

  const handleQuantityChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setQuantity(value);
    setLots(value / (position?.lotSize || 1));
  };

  if (!position) return null;

  return (
    <>
      <ToastContainer />
      {isOpen && <div className="backdrop" onClick={handleBackdropClick} />}
      {isOpen && (
        <animated.div {...bind()} style={{ styles }} className={`close-position-bottom-sheet ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
          {/* Dark Green Bar */}
          {/* <div className="close-position-green-bar"></div> */}
          
          {/* Tabs */}
          <div className="close-position-tabs">
            <div 
              className={`close-position-tab ${orderExecutionType === 'Market' ? 'active' : ''}`}
              onClick={() => handleOrderTypeToggle('Market')} >
              Market
            </div>
            <div 
              className={`close-position-tab ${orderExecutionType === 'Limit' ? 'active' : ''}`}
              onClick={() => handleOrderTypeToggle('Limit')} >
              Limit
            </div>
            <div 
              className={`close-position-tab ${orderExecutionType === 'SL' ? 'active' : ''}`}
              onClick={() => handleOrderTypeToggle('SL')} >
              SL
            </div>
          </div>

          {/* Instrument Name */}
          <div className="close-position-instrument">
            {position.dispname || position.stockName}
            {position.expiry && ` ${position.expiry}`}
          </div>

          {/* Input Fields */}
          <div className="close-position-inputs">
            <div className="close-position-input-group">
              <label className="close-position-label">LOT</label>
              <input
                type="number"
                className="close-position-input"
                value={lots.toFixed(1)}
                onChange={handleLotsChange}
                step="0.1"
                min="0"
              />
            </div>
            <div className="close-position-input-group">
              <label className="close-position-label">QTY</label>
              <input
                type="number"
                className="close-position-input"
                value={quantity}
                onChange={handleQuantityChange}
                min="0"
              />
            </div>
          </div>

          {/* Rate Display */}
          <div className="close-position-rate">
            <div className="close-position-rate-label">Rate</div>
            <div className="close-position-rate-value">
              {orderExecutionType === 'Market' 
                ? (position.buyQty > 0 ? _Bid.toFixed(2) : _Ask.toFixed(2))
                : price || '0.00'
              }
            </div>
          </div>

          {/* Price Input for Limit/SL */}
          {orderExecutionType !== 'Market' && (
            <div className="close-position-price-input">
              <input
                type="number"
                className="close-position-price-field"
                placeholder="Enter Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                step="0.01"
                min="0"
              />
            </div>
          )}

          {/* Swipe to Sell Button */}
          <div className="close-position-action">
            <button 
              className="close-position-sell-btn"
              disabled={disableBtn}
              onClick={createOrder}
            >
              <div className="close-position-swipe-area">
                <ArrowForwardIcon className="close-position-arrow" />
              </div>
              <span className="close-position-btn-text">
                Swipe To {position.buyQty > 0 ? 'Sell' : 'Buy'}
              </span>
            </button>
          </div>
        </animated.div>
      )}
    </>
  );
};

export default BottomSheetClosePosition;
